import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";

export type MarketingBlogArticleCard = {
  id: string;
  title: string;
  description: string;
  href: string;
  imageSrc?: string;
  category?: string;
  meta?: string;
};

export type MarketingBlogArticlesProps = {
  heading?: string;
  items: MarketingBlogArticleCard[];
  className?: string;
};

export function MarketingBlogArticles({
  heading,
  items,
  className,
}: Readonly<MarketingBlogArticlesProps>) {
  if (items.length === 0) return null;

  return (
    <section
      className={clsx("flex justify-center px-4 pb-16 md:pb-24", className)}
    >
      <div className="w-full max-w-6xl rounded-2xl border border-black/5 bg-marketing-industry-card px-6 py-8 md:px-10">
        {heading ? (
          <h2 className="font-monument text-2xl font-medium tracking-tight text-black">
            {heading}
          </h2>
        ) : null}

        <div
          className={clsx(
            "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3",
            heading ? "mt-8" : null,
          )}
        >
          {items.map((item) => (
            <Link
              key={item.id}
              href={item.href}
              className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm transition-shadow hover:shadow-md"
            >
              {item.imageSrc ? (
                <div className="relative aspect-1200/630 w-full overflow-hidden bg-marketing-voice-card">
                  <Image
                    src={item.imageSrc}
                    alt={item.title}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    unoptimized={
                      item.imageSrc.includes("/opengraph-image") ||
                      item.imageSrc.startsWith("/api/studio/files/")
                    }
                  />
                </div>
              ) : null}

              <div className="flex flex-1 flex-col p-6">
                {item.category ? (
                  <p className="font-monument text-xs font-medium uppercase tracking-wide text-gail-primary">
                    {item.category}
                  </p>
                ) : null}
                <h3 className="mt-2 line-clamp-2 font-monument text-lg font-medium leading-snug text-black">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 font-monument text-sm leading-relaxed text-gail-gray">
                  {item.description}
                </p>
                {item.meta ? (
                  <p className="mt-4 font-monument text-xs text-gail-gray">
                    {item.meta}
                  </p>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
